import { Actions } from 'vuex-smart-module';
import { FiltersPagination, Filters } from '@/types';
import { AxiosResponse } from 'axios';
import { ResponseStatuses } from '@/enums';
import ConferencesState from './state';
import ConferencesMutations from './mutations';
import ConferencesGetters from './getters';
import { ConferenceService } from '../services';

const conferenceService = new ConferenceService();

export default class ConferencesActions extends Actions<
ConferencesState,
  ConferencesGetters,
  ConferencesMutations,
  // eslint-disable-next-line no-use-before-define
  ConferencesActions
> {
  async get_conferences(storage_limit?: number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.filters,
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_CONFERENCES_LOADING(true);
      const { data, meta } = await conferenceService.get_conferences(payload);

      this.mutations.SET_CONFERENCES(data.items);
      this.mutations.SET_ORGANIZATION(data.organizations);
      this.mutations.SET_STATUSES(data.statuses);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_CONFERENCES_LOADING(false);
    }
  }

  async get_conference_by_id(id: string | number) {
    try {
      const { data } = await conferenceService.get_conference_by_id(id, { skipInternalError: true });

      this.mutations.SET_NEW_CONFERENCE_RESPONSE(data);
    } catch (error) {
      console.error(this.state.show_not_found_dialog);

      if ((error as AxiosResponse).data.code === ResponseStatuses.Fail.NOT_FOUND) {
        this.mutations.TOGGLE_NOT_FOUND_DIALOG(true);
      }

      if ((error as AxiosResponse).data.code === ResponseStatuses.Fail.BAD_REQUEST) {
        this.mutations.TOGGLE_ROLE_DIALOG(true);
        this.mutations.SET_ROLE_DIALOG((error as AxiosResponse).data.message);
      }
    }
  }

  async get_conference_request_by_id(id: string | number) {
    this.mutations.TOGGLE_USERS_LOADING(true);

    try {
      const data = await conferenceService.get_conference_request_by_id(id);

      this.mutations.SET_CONFERENCE_DETAIL(data.data);

      return data.code;
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_USERS_LOADING(false);
    }
  }

  async send_conference() {
    const payload = this.state.new_conference;

    try {
      const code = await conferenceService.send_conference(payload);

      if (code === ResponseStatuses.Success.CREATED) {
        this.mutations.TOGGLE_SUCCESS_DIALOG(true);
      }

      return code;
    } catch (error) {
      console.error(error);
      return error;
    }
  }

  async upd_conference(id: string | null) {
    const payload = this.state.new_conference;

    try {
      const code = await conferenceService.upd_conference(id, payload);

      if (code === ResponseStatuses.Success.UPDATED) {
        this.mutations.TOGGLE_SUCCESS_DIALOG(true);
        this.mutations.TOGGLE_EDIT_SUCCESS_DIALOG(true);
      }

      return code;
    } catch (error) {
      console.error(error);
      return error;
    }
  }

  async record_stat(id: string | number) {
    try {
      const code = await conferenceService.record_stat(id);

      return code;
    } catch (error) {
      console.error(error);
      return error;
    }
  }
}
